class AnalyticsService {
  constructor() {
    this.baseUrl = this.getApiUrl();
  }

  getApiUrl() {
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL;
    }
    
    if (typeof window !== 'undefined') {
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return 'http://localhost:8000';
      }
      
      // Production: Use same protocol and host as frontend
      const isHttps = window.location.protocol === 'https:';
      return `${isHttps ? 'https' : 'http'}://${window.location.hostname}`;
    }
    
    return 'http://localhost:8000'; // Fallback
  }

  async analyzePortfolio(portfolioData) {
    try {
      // Use the same approach as FloatingAIAssistant
      const authToken = localStorage.getItem('userToken'); // Use 'userToken' like FloatingAIAssistant
      console.log('🔑 Auth token found:', !!authToken);
      
      if (!authToken) {
        throw new Error('Authentication token not found');
      }

      console.log('📊 Calling MCP API for portfolio analysis...');
      console.log('📋 Portfolio data:', portfolioData);

      // Create a comprehensive prompt for portfolio analysis
      const analysisPrompt = `Please analyze my investment portfolio and provide educational insights. Here's my current portfolio data:

Net Worth: $${portfolioData?.netWorth || 0}
Cash: $${portfolioData?.cash || 0}
Investments: $${portfolioData?.totalAssetsValue || 0}
Loan: $${portfolioData?.loan || 0}
Savings: $${portfolioData?.deposit || 0}

Please provide:
1. A summary of my portfolio's current state
2. What I'm doing well (strengths)
3. Recommendations for improvement
4. Risk assessment

Keep it educational and age-appropriate for young investors.`;

      // Use the same endpoint as FloatingAIAssistant with environment-based URL
      const apiUrl = this.baseUrl;
      const response = await fetch(`${apiUrl}/api/agent/ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          question: analysisPrompt
        })
      });

      console.log('🌐 API Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ Claude Agent Response:', data);
      
      if (data.success) {
        // Transform the Claude response to match our expected format
        const transformedData = {
          summary: data.message || 'Portfolio analysis completed.',
          strengths: this.extractStrengths(data.message || ''),
          recommendations: this.extractRecommendations(data.message || ''),
          riskAssessment: {
            level: this.extractRiskLevel(data.message || '', portfolioData),
            factors: this.extractRiskFactors(portfolioData)
          }
        };

        console.log('🔄 Transformed data:', transformedData);

        return {
          success: true,
          data: transformedData
        };
      } else {
        throw new Error(data.message || 'Failed to get response');
      }
    } catch (error) {
      console.error('❌ Error analyzing portfolio:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async askFollowUpQuestion(portfolioData, question, previousContext = null) {
    try {
      // Use the same approach as FloatingAIAssistant
      const authToken = localStorage.getItem('userToken'); // Use 'userToken' like FloatingAIAssistant
      console.log('🔑 Auth token found for follow-up:', !!authToken);
      
      if (!authToken) {
        throw new Error('Authentication token not found');
      }

      console.log('💬 Asking follow-up question:', question);

      // Create context-aware question with portfolio data
      const contextualQuestion = `Based on my portfolio data (Net Worth: $${portfolioData?.netWorth || 0}, Cash: $${portfolioData?.cash || 0}, Investments: $${portfolioData?.totalAssetsValue || 0}, Loan: $${portfolioData?.loan || 0}), ${question}`;

      // Use the same endpoint as FloatingAIAssistant with environment-based URL
      const apiUrl = this.baseUrl;
      const response = await fetch(`${apiUrl}/api/agent/ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          question: contextualQuestion
        })
      });

      console.log('🌐 Follow-up API Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Follow-up API Error:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ Follow-up Claude Response:', data);
      
      if (data.success) {
        return {
          success: true,
          data: {
            answer: data.message || 'Analysis completed.'
          }
        };
      } else {
        throw new Error(data.message || 'Failed to get response');
      }
    } catch (error) {
      console.error('❌ Error with follow-up question:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Generate sample analysis for development/demo purposes
  generateSampleAnalysis(portfolioData) {
    const netWorth = portfolioData?.netWorth || 0;
    const cash = portfolioData?.cash || 0;
    const totalAssetsValue = portfolioData?.totalAssetsValue || 0;
    const loan = portfolioData?.loan || 0;
    
    const cashPercentage = netWorth > 0 ? ((cash / netWorth) * 100).toFixed(1) : 0;
    const investmentPercentage = netWorth > 0 ? ((totalAssetsValue / netWorth) * 100).toFixed(1) : 0;
    
    return {
      summary: `Your portfolio shows a net worth of $${netWorth.toFixed(2)} with ${cashPercentage}% in cash and ${investmentPercentage}% in investments. ${loan > 0 ? `You have an outstanding loan of $${loan.toFixed(2)}.` : 'No outstanding loans.'}`,
      
      strengths: [
        cash > 1000 ? "Good cash reserves for future opportunities" : "Building cash position",
        totalAssetsValue > 0 ? "Diversified investment portfolio" : "Ready to start investing",
        loan === 0 ? "Debt-free portfolio" : "Managing debt responsibly"
      ],
      
      recommendations: [
        cash > netWorth * 0.5 ? "Consider investing some cash for growth" : "Maintain emergency fund",
        totalAssetsValue < netWorth * 0.3 ? "Explore investment opportunities" : "Monitor portfolio performance",
        "Continue learning about financial markets"
      ],
      
      riskAssessment: {
        level: loan > netWorth * 0.3 ? "Medium" : "Low",
        factors: [
          `Cash ratio: ${cashPercentage}%`,
          `Investment ratio: ${investmentPercentage}%`,
          `Debt ratio: ${loan > 0 ? ((loan / netWorth) * 100).toFixed(1) : 0}%`
        ]
      }
    };
  }

  // Helper methods to extract structured data from Claude responses
  extractStrengths(message) {
    const strengths = [];
    
    // Look for positive indicators in the message
    if (message.includes('cash') || message.includes('$1,000')) {
      strengths.push("Good starting cash position");
    }
    if (message.includes('debt-free') || message.includes('no loans')) {
      strengths.push("Debt-free portfolio");
    }
    if (message.includes('diversif') || message.includes('spread')) {
      strengths.push("Understanding diversification concepts");
    }
    if (message.includes('research') || message.includes('learning')) {
      strengths.push("Commitment to learning and research");
    }
    
    // Default strengths if none found
    if (strengths.length === 0) {
      strengths.push("Ready to start investing journey");
      strengths.push("Educational approach to investing");
    }
    
    return strengths;
  }

  extractRecommendations(message) {
    const recommendations = [];
    
    // Look for suggestion keywords in the message
    if (message.includes('start small') || message.includes('$200-300')) {
      recommendations.push("Start with smaller investments to practice");
    }
    if (message.includes('diversif') || message.includes('2-3 different')) {
      recommendations.push("Consider diversifying across different companies");
    }
    if (message.includes('research') || message.includes('companies you know')) {
      recommendations.push("Research companies you're familiar with");
    }
    if (message.includes('split your money') || message.includes('spread')) {
      recommendations.push("Don't put all your money in one investment");
    }
    
    // Default recommendations if none found
    if (recommendations.length === 0) {
      recommendations.push("Start with companies you know and use");
      recommendations.push("Begin with small investments to learn");
      recommendations.push("Continue learning about investing fundamentals");
    }
    
    return recommendations;
  }

  extractRiskLevel(message, portfolioData) {
    const cash = portfolioData?.cash || 0;
    const totalAssetsValue = portfolioData?.totalAssetsValue || 0;
    const loan = portfolioData?.loan || 0;
    const netWorth = portfolioData?.netWorth || 0;
    
    // Calculate risk based on portfolio composition
    const cashRatio = netWorth > 0 ? cash / netWorth : 1;
    const debtRatio = netWorth > 0 ? loan / netWorth : 0;
    
    if (debtRatio > 0.3) return "Medium";
    if (cashRatio > 0.8) return "Very Low";
    if (totalAssetsValue === 0) return "Very Low";
    
    return "Low";
  }

  extractRiskFactors(portfolioData) {
    const netWorth = portfolioData?.netWorth || 0;
    const cash = portfolioData?.cash || 0;
    const totalAssetsValue = portfolioData?.totalAssetsValue || 0;
    const loan = portfolioData?.loan || 0;
    
    const cashPercentage = netWorth > 0 ? ((cash / netWorth) * 100).toFixed(1) : 100;
    const investmentPercentage = netWorth > 0 ? ((totalAssetsValue / netWorth) * 100).toFixed(1) : 0;
    const debtPercentage = netWorth > 0 ? ((loan / netWorth) * 100).toFixed(1) : 0;
    
    return [
      `Cash ratio: ${cashPercentage}%`,
      `Investment ratio: ${investmentPercentage}%`,
      `Debt ratio: ${debtPercentage}%`
    ];
  }
}

export default new AnalyticsService();